import { useRef } from "react";
import { MapPinned, Trash2, Plus } from "lucide-react";

const cellCls = "border border-ptf-primary/20 px-3 py-2 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text";

// Route editor: ordered stops {name, lat, lng, mode}. mode = how you travel TO this stop.
export default function RouteEditor({ value = [], onChange }) {
  const stops = Array.isArray(value) ? value : [];
  // Stable per-row keys (not persisted to the stop data itself) so React doesn't
  // mis-reuse input DOM nodes when a stop is removed from the middle of the list.
  const nextIdRef = useRef(0);
  const idsRef = useRef([]);
  if (idsRef.current.length !== stops.length) {
    const ids = idsRef.current.slice(0, stops.length);
    while (ids.length < stops.length) ids.push(`stop-${nextIdRef.current++}`);
    idsRef.current = ids;
  }
  const setStop = (i, k, v) => onChange(stops.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const removeAt = (i) => {
    idsRef.current = idsRef.current.filter((_, idx) => idx !== i);
    onChange(stops.filter((_, idx) => idx !== i));
  };
  const addStop = () => {
    idsRef.current = [...idsRef.current, `stop-${nextIdRef.current++}`];
    onChange([...stops, { name: "", lat: "", lng: "", mode: "road" }]);
  };

  return (
    <div data-testid="route-editor">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Route map stops (in travel order)</span>
      <div className="mt-1 space-y-2">
        {stops.map((s, i) => (
          <div key={idsRef.current[i] ?? i} data-testid="route-editor-item" className="border border-ptf-primary/15 bg-white p-2">
            <div className="flex items-center gap-2">
              <span className="shrink-0 grid place-items-center h-8 w-8 bg-ptf-primary/5 text-ptf-primary"><MapPinned className="h-4 w-4" /></span>
              <input value={s.name} onChange={(e) => setStop(i, "name", e.target.value)} placeholder={`Stop ${i + 1} name *`} data-testid="route-name" className={`${cellCls} flex-1 min-w-0 w-auto`} />
              <button type="button" onClick={() => removeAt(i)} data-testid="route-remove" className="grid h-8 w-8 shrink-0 place-items-center border border-ptf-primary/15 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 pl-10">
              <input type="number" step="any" value={s.lat} onChange={(e) => setStop(i, "lat", e.target.value)} placeholder="Latitude" data-testid="route-lat" className={`${cellCls} flex-1 min-w-0 w-auto`} />
              <input type="number" step="any" value={s.lng} onChange={(e) => setStop(i, "lng", e.target.value)} placeholder="Longitude" data-testid="route-lng" className={`${cellCls} flex-1 min-w-0 w-auto`} />
              <select value={s.mode || "road"} onChange={(e) => setStop(i, "mode", e.target.value)} data-testid="route-mode" className={`${cellCls} w-32 shrink-0`} title="How you travel to this stop">
                <option value="road">Overland</option>
                <option value="fly">By air</option>
              </select>
            </div>
          </div>
        ))}
        <button type="button" onClick={addStop} data-testid="route-add" className="inline-flex items-center gap-2 border border-dashed border-ptf-primary/30 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-ptf-secondary hover:bg-ptf-primary/5 transition-colors">
          <Plus className="h-4 w-4" /> Add stop
        </button>
        <p className="text-[0.62rem] text-ptf-text/50">Tip: latitude/longitude from Google Maps (right-click a place → copy coordinates). "Mode" is how travellers reach that stop from the previous one.</p>
      </div>
    </div>
  );
}
